import { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Volume2, VolumeX, Heart, MessageCircle, Share2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Golf shot swoosh sound (base64 encoded short swoosh)
const SWOOSH_SOUND_URL = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYNNfL9AAAAAAAAAAAAAAAAAAAAAP/7kGQAAANUAEABBAAAgAAA0gAAABHhqZ/gAAA0gAAABMFBAVBAQEBAULuogICAgouoiNBAQEBAULuogICAgouoiNBQu6iAAAAABQu6iI0EBAQEBQu6iAgICC';

const VideoReelViewer = ({ 
  videos, 
  initialIndex = 0, 
  isOpen, 
  onClose,
  onLike,
  onComment,
  onShare
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [direction, setDirection] = useState(0); // -1 = left, 1 = right
  const [isMuted, setIsMuted] = useState(false); // Start unmuted so users can hear audio
  const [soundEnabled, setSoundEnabled] = useState(false);
  const containerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const audioRef = useRef(null);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(SWOOSH_SOUND_URL);
    audioRef.current.volume = 0.3;
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Play swoosh sound
  const playSwoosh = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  // Navigate to next video
  const goToNext = useCallback(() => {
    if (currentIndex < videos.length - 1) {
      playSwoosh();
      setDirection(1);
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex, videos.length, playSwoosh]);

  // Navigate to previous video
  const goToPrev = useCallback(() => {
    if (currentIndex > 0) {
      playSwoosh();
      setDirection(-1);
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex, playSwoosh]);

  // Handle touch start
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  // Handle touch end (swipe detection)
  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const diffX = touchStartX.current - touchEndX;
    const diffY = touchStartY.current - touchEndY;

    // Only register horizontal swipes (ignore vertical)
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      if (diffX > 0) {
        // Swiped left = next (like a golf swing follow-through)
        goToNext();
      } else {
        // Swiped right = previous
        goToPrev();
      }
    }
    
    // Swipe down to close
    if (diffY < -100 && Math.abs(diffY) > Math.abs(diffX)) {
      onClose();
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrev();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, goToNext, goToPrev, onClose]);

  // Prevent body scroll when viewer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset index when videos change
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, videos]);

  if (!isOpen || !videos || videos.length === 0) return null;

  const currentVideo = videos[currentIndex];

  // Golf shot animation variants - parabolic arc trajectory
  const golfShotVariants = {
    // Entry animation (ball landing)
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      y: -200,
      opacity: 0,
      scale: 0.8,
      rotate: direction > 0 ? 15 : -15,
    }),
    // Center position (at rest)
    center: {
      x: 0,
      y: 0,
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
        mass: 1,
      }
    },
    // Exit animation (ball flying away)
    exit: (direction) => ({
      x: direction > 0 ? -400 : 400,
      y: -250,
      opacity: 0,
      scale: 0.6,
      rotate: direction > 0 ? -20 : 20,
      transition: {
        type: "tween",
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1], // Custom easing for golf shot feel
      }
    }),
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black flex flex-col"
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center justify-between">
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="close-video-viewer"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          
          <div className="flex items-center gap-2">
            {/* Sound toggle for transition effects */}
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              title={soundEnabled ? "Mute swoosh" : "Enable swoosh sound"}
            >
              {soundEnabled ? (
                <Volume2 className="w-5 h-5 text-white" />
              ) : (
                <VolumeX className="w-5 h-5 text-white/50" />
              )}
            </button>
            
            {/* Video counter */}
            <div className="px-3 py-1 rounded-full bg-white/10 text-white text-sm font-medium">
              {currentIndex + 1} / {videos.length}
            </div>
          </div>
        </div>
      </div>

      {/* Progress dots */}
      <div className="absolute top-16 left-0 right-0 z-10 flex justify-center gap-1 px-4">
        {videos.map((_, idx) => (
          <div
            key={idx}
            className={`h-1 rounded-full transition-all duration-300 ${
              idx === currentIndex 
                ? 'bg-white w-6' 
                : 'bg-white/30 w-1.5'
            }`}
          />
        ))}
      </div>

      {/* Main video area with golf shot animation */}
      <div className="flex-1 flex items-center justify-center overflow-hidden px-4">
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.div
            key={currentIndex}
            custom={direction}
            variants={golfShotVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="w-full max-w-2xl"
          >
            {/* Video embed container - Full width for better mobile experience */}
            <div className="relative rounded-2xl overflow-hidden bg-gray-900 shadow-2xl">
              {/* YouTube/TikTok iframe */}
              {currentVideo.platform === 'youtube' && (
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${currentVideo.id}?autoplay=1&mute=${isMuted ? 1 : 0}&modestbranding=1&rel=0&playsinline=1`}
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Video"
                  />
                </div>
              )}
              
              {currentVideo.platform === 'tiktok' && (
                <div className="relative w-full" style={{ paddingBottom: '125%', maxHeight: '70vh' }}>
                  <iframe
                    src={currentVideo.embedUrl}
                    className="absolute inset-0 w-full h-full"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    title="TikTok video"
                  />
                </div>
              )}

              {/* Generic video URL */}
              {currentVideo.platform === 'video' && currentVideo.url && (
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <video
                    src={currentVideo.url}
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay
                    loop
                    muted={isMuted}
                    playsInline
                    controls
                  />
                </div>
              )}
            </div>

            {/* Post info below video */}
            <div className="mt-3 text-white px-1">
              {currentVideo.userName && (
                <p className="font-bold text-base">{currentVideo.userName}</p>
              )}
              {currentVideo.content && (
                <p className="text-white/70 text-sm mt-1 line-clamp-2">{currentVideo.content}</p>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Side action buttons */}
      <div className="absolute right-4 bottom-32 flex flex-col gap-4 z-10">
        {/* Mute/Unmute Button */}
        <button
          onClick={() => setIsMuted(!isMuted)}
          className="flex flex-col items-center gap-1"
        >
          <div className={`p-3 rounded-full transition-colors ${isMuted ? 'bg-red-500/30' : 'bg-white/10 hover:bg-white/20'}`}>
            {isMuted ? (
              <VolumeX className="w-6 h-6 text-white" />
            ) : (
              <Volume2 className="w-6 h-6 text-white" />
            )}
          </div>
          <span className="text-white text-xs">{isMuted ? 'Unmute' : 'Sound'}</span>
        </button>
        
        {onLike && (
          <button
            onClick={() => onLike(currentVideo.postId)}
            className="flex flex-col items-center gap-1"
          >
            <div className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
              <Heart className={`w-6 h-6 ${currentVideo.isLiked ? 'text-red-500 fill-current' : 'text-white'}`} />
            </div>
            <span className="text-white text-xs">{currentVideo.likesCount || 0}</span>
          </button>
        )}
        
        {onComment && (
          <button
            onClick={() => onComment(currentVideo.postId)}
            className="flex flex-col items-center gap-1"
          >
            <div className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <span className="text-white text-xs">{currentVideo.commentsCount || 0}</span>
          </button>
        )}
        
        {onShare && (
          <button
            onClick={() => onShare(currentVideo.postId)}
            className="flex flex-col items-center gap-1"
          >
            <div className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
              <Share2 className="w-6 h-6 text-white" />
            </div>
            <span className="text-white text-xs">Share</span>
          </button>
        )}
      </div>

      {/* Navigation hints / tap zones - higher z-index to work over video */}
      <div className="absolute inset-y-0 left-0 w-16 z-30" onClick={goToPrev}>
        {currentIndex > 0 && (
          <div className="absolute left-2 top-1/2 -translate-y-1/2">
            <div className="p-2 rounded-full bg-black/40">
              <ChevronLeft className="w-6 h-6 text-white" />
            </div>
          </div>
        )}
      </div>
      <div className="absolute inset-y-0 right-0 w-16 z-30" onClick={goToNext}>
        {currentIndex < videos.length - 1 && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <div className="p-2 rounded-full bg-black/40">
              <ChevronRight className="w-6 h-6 text-white" />
            </div>
          </div>
        )}
      </div>

      {/* Bottom hint */}
      <div className="absolute bottom-8 left-0 right-0 text-center z-10">
        <p className="text-white/40 text-xs">
          {currentIndex < videos.length - 1 
            ? '← Swipe to continue →' 
            : 'End of videos'}
        </p>
      </div>
    </motion.div>
  );
};

export default VideoReelViewer;
